import React from 'react';

import EmailTriageGame from './EmailTriageGame';
import SwipeGame from './SwipeGame';
import ChatHeroGame from './ChatHeroGame';
import ResumeRoaster from './ResumeRoaster';
import BattleRoyaleGame from './BattleRoyaleGame'; 

// --- High-Fidelity Logic Imports ---
import AiAuditGame from './AiAuditGame'; 
import NegotiationGame from './NegotiationGame';
import LeaseDefender from './LeaseDefender';
import PromptGolfGame from './PromptGolfGame';

// --- Resilience & Spatial Audit Imports ---
import ResilienceEngine from '../../../components/shared/ResilienceEngine';
import DynamicSJTGame from './DynamicSJTGame';
import SpatialAuditGame from './SpatialAuditGame';
import ChronosEngineGame from './ChronosEngineGame';

/**
 * ReinforcementComponent
 * The central router for all interactive curriculum exercises.
 * Updated to wrap active simulations in the Resilience Engine (Friction-Based Pedagogy).
 */
const ReinforcementComponent = ({ 
  type, 
  data, 
  onComplete, 
  onBack, 
  isReviewMode, 
  onIncorrectAttempt 
}) => {

  // --- STRATEGIC HELPER: RESILIENCE WRAPPER ---
  // Wraps interactive games in the 3-Tier Struggle Flow.
  // Bypassed during Review Mode to allow frictionless studying.
  const renderWithResilience = (Component, props) => {
    if (isReviewMode) {
        return <Component {...props} onComplete={onComplete} />;
    }
    return (
        <ResilienceEngine onModuleComplete={onComplete} moduleName={data?.title}>
            <Component {...props} />
        </ResilienceEngine>
    );
  };

  // --- 0. SPATIAL AUDIT / MODERN NETWORKING (Masking Engine) ---
  if (type === 'spatialAudit' || type === 'networking') {
    return renderWithResilience(SpatialAuditGame, { gameData: data?.content, onBack });
  }

  // --- 1. AI AUDIT & CONTRACTUAL AMBIGUITY AUDIT (Truth Detective Logic) ---
  if (type === 'aiAudit') {
    return renderWithResilience(AiAuditGame, { gameData: data?.content });
  }

  // --- 2. NEGOTIATION SIMULATOR ---
  if (type === 'negotiation') {
    return renderWithResilience(NegotiationGame, { gameData: data?.content });
  }

  // --- 3. CONTRACTUAL AMBIGUITY AUDIT ---
  if (type === 'leaseDefender') {
    return renderWithResilience(LeaseDefender, { gameData: data?.content });
  }

  // --- 4. PROMPT GOLF (Human-in-the-Loop) ---
  if (type === 'promptGolf') {
    return renderWithResilience(PromptGolfGame, {});
  }

  // --- 5. UNTIMED DYNAMIC SJT (LEADERSHIP MICRO) ---
  if (type === 'dynamicSJT') {
    return renderWithResilience(DynamicSJTGame, { gameData: data?.content });
  }

  // --- 6. SPLIT-SCREEN TRIAGE (CHRONOS ENGINE) ---
  if (type === 'chronosAudit') {
    return renderWithResilience(ChronosEngineGame, { gameData: data?.content, onBack });
  }

  // --- STANDARD GAME TYPES ---
  if (type === 'battleRoyale') {
    return renderWithResilience(BattleRoyaleGame, { gameData: data?.content });
  }

  if (type === 'roaster') {
    return renderWithResilience(ResumeRoaster, { gameData: data?.content });
  }

  if (type === 'chatHero') {
    return renderWithResilience(ChatHeroGame, { gameData: data?.content });
  }

  if (type === 'swipe') {
    return renderWithResilience(SwipeGame, { gameData: data?.content });
  }

  if (type === 'emailGame') {
    return renderWithResilience(EmailTriageGame, { gameData: data?.content, onBack });
  }
  
  return (
    <div className="p-8 text-center bg-red-50 rounded-xl border border-red-200">
      <p className="text-red-600 font-bold uppercase tracking-widest text-xs">Router Error</p>
      <p className="text-gray-700 mt-2 font-medium text-sm">Reinforcement type "{type}" is not registered.</p>
    </div>
  );
};

export default ReinforcementComponent;